import { useMemo, useState } from 'react'
import { Link } from 'react-router'

import { NAVIGATION, flattenNavigation } from './navigation'

import { Dialog, Input } from '@/components/primitives'
import { Icon, NAV_ICONS } from '@/components/primitives/Icon'
import type { Permission } from '@/lib/permissions/catalog'
import { useCan } from '@/lib/permissions/useCan'
import { cn } from '@/lib/utils/cn'

interface Destination {
  label: string
  to: string
  parent?: string
  permission?: Permission
  tone?: string
  icon: string
}

/**
 * Jump-to-page search.
 *
 * Searches SUB-destinations as well, so "invoices" finds Billing → Invoices
 * and "breached" finds the SLA queue. Typing the name of a filter and landing
 * on the filter is the whole point of the palette; landing on the parent
 * screen and hunting for a chip is not.
 */
export function CommandPalette({
  workspaceSlug,
  onClose,
}: {
  workspaceSlug: string
  onClose: () => void
}) {
  const [query, setQuery] = useState('')
  const needle = query.trim().toLowerCase()

  const destinations = useMemo(() => flattenNavigation(NAVIGATION), [])
  const matches = destinations.filter(
    (d) =>
      d.label.toLowerCase().includes(needle) ||
      (d.parent?.toLowerCase().includes(needle) ?? false),
  )

  return (
    <Dialog
      open
      onClose={onClose}
      title="Jump to a page"
      description="Find your next destination in this workspace."
    >
      <Input
        label="Search pages"
        placeholder="Try chatbots, invoices, or breached…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        leading={<Icon name="search" size={18} />}
      />
      <ul className="command-results mt-4">
        {matches.map((destination) => (
          <Result
            key={`${destination.parent ?? ''}${destination.to}`}
            destination={destination}
            workspaceSlug={workspaceSlug}
            onClose={onClose}
          />
        ))}
        {/* Hidden by CSS as soon as one result survives its permission check,
            which a `matches.length` test in here could not see. */}
        <li className="command-empty text-compact text-text-secondary">
          No accessible pages match your search. Try another name.
        </li>
      </ul>
      <p className="mt-3 text-caption text-text-tertiary">
        Use Tab to browse results, Enter to open, and Esc to close.
      </p>
    </Dialog>
  )
}

function Result({
  destination,
  workspaceSlug,
  onClose,
}: {
  destination: Destination
  workspaceSlug: string
  onClose: () => void
}) {
  const allowed = useCan(...(destination.permission ? [destination.permission] : []))
  if (!allowed) return null
  return (
    <li>
      <Link
        to={`/w/${workspaceSlug}${destination.to}`}
        onClick={onClose}
        className={cn('command-result', destination.tone && `nav-tone-${destination.tone}`)}
      >
        <span className="command-result-chip" aria-hidden="true">
          <Icon name={NAV_ICONS[destination.icon] ?? 'dashboard'} size={17} />
        </span>
        <span className="command-result-label">
          {destination.parent && (
            <span className="command-result-parent">{destination.parent}</span>
          )}
          <span>{destination.label}</span>
        </span>
        <Icon name="arrow" size={16} />
      </Link>
    </li>
  )
}
